/**
 * High level router.
 *
 * Note: It's recommended to compose related routes in internal router
 * components (e.g: `src/app/modules/Auth/pages/AuthPage`, `src/app/BasePage`).
 */

import React, { useEffect, useState } from "react";
import { Redirect, Switch, Route, useLocation } from "react-router-dom";
import { shallowEqual, useSelector, useDispatch } from "react-redux";
import { Layout, LayoutSplashScreen } from "../_metronic/layout";
import BasePage from "./BasePage";
import { Logout, AuthPage } from "./modules/Auth";
import ErrorsPage from "./modules/ErrorsExamples/ErrorsPage";
import { saveLastLocation, getLastLocation } from "../_metronic/_helpers/RouterHelpers";
import RegistrationPage from "./modules/Registration/RegistrationPage";
import { VERIFICATION_STATUS, ROLES } from "./services/flitEnum";
import { firebaseAuth } from "../firebase"; // ✅ Add this
import { onAuthStateChanged } from "firebase/auth"; // ✅ Add this
import * as auth from "./modules/Auth/_redux/authRedux"; // ✅ Add this
import SetPassword from "./modules/Auth/pages/SetPassword";

export function Routes(props) {
    const dispatch = useDispatch();
    const [authChecked, setAuthChecked] = useState(false); // ✅ Add loading state
    
    const { isAuthorized, isLoading, businessEntity, isOperator } = useSelector(
    ({ auth }) => ({
        isAuthorized: auth.user != null && auth.authToken != null,
        isLoading: auth.isLoading, // ✅ Use this
        businessEntity: auth.user?.businessEntity,
        isOperator: auth.user?.userRole?.some(r => [ROLES.OPERATOR, ROLES.MANAGER, ROLES.DEVELOPER].includes(r.role)),
    }),
    shallowEqual
);
    
    const location = useLocation();
    saveLastLocation(location);
    const lastLocation = getLastLocation();

    // ✅ Check Firebase auth state on mount (handles page refresh)
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(firebaseAuth, async (user) => {
            if (user) {
                // User is logged in via Firebase
                console.log('✅ Firebase auth restored:', user.email);
                
                // Get fresh token and store it
                const token = await user.getIdToken();
                localStorage.setItem('firebaseToken', token);
                
                // If Redux doesn't know about the user yet, restore the session
                if (!isAuthorized) {
                    console.log('🔄 Restoring user session from Firebase...');
                    dispatch(auth.actions.login());
                    dispatch(auth.actions.updateToken());
                }
                
                setAuthChecked(true);
            } else {
                // No Firebase user - check for WeChat JWT
                const wechatToken = localStorage.getItem('jwt');
                
                if (wechatToken && !isAuthorized) {
                    console.log('🔄 Restoring WeChat session...');
                    dispatch(auth.actions.login());
                    dispatch(auth.actions.updateToken());
                }
                
                setAuthChecked(true);
            }
        });

        // Timeout fallback in case Firebase takes too long
        const timeout = setTimeout(() => {
            if (!authChecked) {
                console.log('⏱️ Firebase auth check timeout, proceeding...');
                setAuthChecked(true);
            }
        }, 3000);

        return () => {
            unsubscribe();
            clearTimeout(timeout);
        };
    }, [dispatch, isAuthorized, authChecked]);

    // ✅ Show loading screen while checking auth
    if (!authChecked ) {
        return <LayoutSplashScreen />;
    }

    return (
        <Switch>
            <Route path="/logout" component={Logout} />
            <Route path="/error" component={ErrorsPage} />
            <Route path="/registration" component={RegistrationPage} />

            {!isAuthorized || location.pathname.startsWith("/auth/registration") ? (
                /*Render auth page when user at `/auth` and not authorized.*/
                <Route>
                    <AuthPage />
                </Route>
            ) : (
                /*Otherwise redirect to root page (`/`)*/
                isOperator ?
                    <Redirect from="/auth" to={lastLocation.pathname} /> :
                    (!businessEntity?.idNumber || businessEntity?.verificationStatus === VERIFICATION_STATUS.VERIFICATION_FAILED) ?
                        <Redirect to="/registration/business-number" /> :
                        <Redirect from="/auth" to={lastLocation.pathname} />
            )}

            {!isAuthorized ? (
                /*Redirect to `/auth` when user is not authorized*/
                <Redirect to="/auth/login" />
            ) : (
                <Layout>
                    <BasePage />
                </Layout>
            )}
        </Switch>
    );
}